// src/components/forms/InlineFieldRenderer.tsx
import React, { useEffect, useState } from "react";
import {
  TextField,
  Checkbox,
  Select,
  MenuItem,
  FormControlLabel,
  FormControl,
  InputLabel,
  Switch,
  Typography,
  Box,
  Tooltip,
  useTheme,
  Button,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { TableField } from "../../types/interfaces";
import { validateFieldValue } from "../../utils/validators";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import StarIcon from "@mui/icons-material/Star";

interface Props {
  field: TableField;
  value: any;
  onChange: (fieldId: string, newValue: any) => void;
  error?: string;
  autoFocus?: boolean;
  isEditing?: boolean;
}

const InlineFieldRenderer: React.FC<Props> = ({
  field,
  value,
  onChange,
  error,
  autoFocus = false,
  isEditing = false,
}) => {
  const theme = useTheme();
  const [localValue, setLocalValue] = useState<any>(value ?? "");
  const [localError, setLocalError] = useState<string | undefined>(error);
  const [focused, setFocused] = useState(false);

  const label = field.fieldName;
  const isReadOnly = field.readOnly ?? false;
  const isRequired = field.required ?? false;
  const maxLength = field.length;
  const inputMask = field.inputMask ?? "";
  const allowSpecial = field.allowSpecialCharacters ?? true;
  const dataType = (field.dataType || "").toUpperCase();

  useEffect(() => {
    setLocalValue(value ?? "");
    setLocalError(error);
  }, [value, error]);

  const enforceSpecialCharRule = (val: string): boolean => {
    if (allowSpecial) return true;
    return /^[A-Za-z0-9\s]*$/.test(val);
  };

  const handleBlur = () => {
    setFocused(false);
    const validation = validateFieldValue(field, localValue);
    setLocalError(validation.valid ? undefined : validation.message);
    if (validation.valid) {
      onChange(field.fieldID, localValue);
    }
  };

  const handleChange = (e: React.ChangeEvent<any>) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;

    if (typeof val === "string" && !enforceSpecialCharRule(val)) {
      setLocalError("Invalid characters");
      return;
    }

    setLocalValue(val);
    setLocalError(undefined);
  };

  const renderStatusIcon = () => {
    if (!isEditing || focused) return null;
    if (isRequired && !localValue) {
      return <Tooltip title="Required"><StarIcon color="error" fontSize="small" /></Tooltip>;
    }
    if (localError) {
      return <Tooltip title="Invalid"><ErrorIcon color="warning" fontSize="small" /></Tooltip>;
    }
    if (localValue) {
      return <Tooltip title="Valid"><CheckCircleIcon color="success" fontSize="small" /></Tooltip>;
    }
    return null;
  };

  const renderFocusedHint = () => {
    if (!focused) return null;
    if (localError) {
      return (
        <Typography variant="caption" color="warning.main" sx={{ mb: 0.5 }}>
          ❗ {localError}
        </Typography>
      );
    }
    if (isRequired && !localValue) {
      return (
        <Typography variant="caption" color="error.main" sx={{ mb: 0.5 }}>
          * Required
        </Typography>
      );
    }
    return null;
  };

  const renderHelperBelow = () => {
    if (!focused) return null;
    if (inputMask) {
      return (
        <Typography variant="caption" color="text.secondary">
          Format: {inputMask}
        </Typography>
      );
    }
    if (maxLength) {
      return (
        <Typography variant="caption" color="text.secondary">
          Max {maxLength} characters
        </Typography>
      );
    }
    return null;
  };

  const wrapperStyle = {
    transition: "all 0.2s ease-in-out",
    transform: focused ? "scale(1.03)" : "scale(1)",
    boxShadow: focused ? `0 0 4px ${theme.palette.primary.main}` : "none",
    borderRadius: 1,
    padding: focused ? "4px 6px" : "0",
    backgroundColor: focused ? theme.palette.action.hover : "transparent",
  };

// === BOOLEAN / CHECKBOX ===
if (dataType === "BOOLEAN" || dataType === "CHECKBOX") {
  let trueVal = "1";
  let falseVal = "0";
  let labels: string[] = [];

  // Try to use listValues
  if (field?.listValues) {
    try {
      const parsed = JSON.parse(field.listValues);
      if (Array.isArray(parsed) && parsed.length >= 2) {
        trueVal = String(parsed[0]);
        falseVal = String(parsed[1]);
        labels = [trueVal, falseVal];
      }
    } catch {
      console.warn("Invalid listValues JSON for checkbox:", field?.listValues);
    }
  }

  const valueStr = String(localValue ?? "");
  const isCustom = labels.length === 2;

  return (
    <Box sx={wrapperStyle}>
      {isCustom ? (
        <ToggleButtonGroup
          value={valueStr}
          exclusive
          onChange={(_, newVal) => {
            if (newVal !== null) {
              handleChange({ target: { value: newVal } } as any);
            }
          }}
          size="small"
          disabled={isReadOnly}
        >
          <ToggleButton value={trueVal}>{trueVal}</ToggleButton>
          <ToggleButton value={falseVal}>{falseVal}</ToggleButton>
        </ToggleButtonGroup>
      ) : (
        <FormControlLabel
          control={
            <Checkbox
              checked={valueStr === "1"}
              onChange={(e) => {
                const val = e.target.checked ? "1" : "0";
                handleChange({ target: { value: val } } as any);
              }}
              onBlur={handleBlur}
              onFocus={() => setFocused(true)}
              disabled={isReadOnly}
              inputProps={{ "aria-label": label }}
            />
          }
          label={label}
        />
      )}
    </Box>
  );
}

  if (dataType === "SWITCH") {
    return (
      <Box sx={wrapperStyle}>
        <FormControlLabel
          control={
            <Switch
              checked={!!localValue}
              onChange={handleChange}
              onBlur={handleBlur}
              onFocus={() => setFocused(true)}
              disabled={isReadOnly}
            />
          }
          label={label}
        />
      </Box>
    );
  }

  // === DROPDOWN ===
if (["LISTBOX", "COMBOBOX", "DROPDOWN"].includes(dataType)) {
    let options: string[] = [];
  
    try {
      const parsed = typeof field.listValues === "string"
        ? JSON.parse(field.listValues)
        : field.listValues;
  
      options = Array.isArray(parsed)
        ? parsed.map((item: any) => item.toString())
        : [];
    } catch {
      options = [];
    }
  
    return (
      <Box sx={wrapperStyle}>
        {renderFocusedHint()}
        <FormControl fullWidth margin="dense">
          <InputLabel>{label}</InputLabel>
          <Select
            value={localValue}
            onChange={(event) => {
              const newValue = event.target.value;
              setLocalValue(newValue);
              setLocalError(undefined);
            }}
            onBlur={handleBlur}
            onFocus={() => setFocused(true)}
            autoFocus={autoFocus}
            disabled={isReadOnly}
            required={isRequired}
            error={!!localError}
            label={label}
            aria-label={label}
          >
            {options.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {renderHelperBelow()}
      </Box>
    );
  }
  

  // === DATE / TIME ===
  if (["DATE", "TIME", "TIMESTAMP", "DATETIME"].includes(dataType)) {
    const inputType =
      dataType === "TIME" ? "time" :
      dataType === "DATE" ? "date" : "datetime-local";

    return (
      <Box sx={wrapperStyle}>
        {renderFocusedHint()}
        <TextField
          fullWidth
          type={inputType}
          label={label}
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={() => setFocused(true)}
          InputLabelProps={{ shrink: true }}
          autoFocus={autoFocus}
          disabled={isReadOnly}
          required={isRequired}
          error={!!localError}
          margin="dense"
          aria-label={label}
        />
        {renderHelperBelow()}
      </Box>
    );
  }

  // === FILE / BLOB ===
  if (["FILE", "BLOB"].includes(dataType)) {
    const fileMime = typeof value === "string"
      ? value.split(";")[0]?.replace("data:", "")
      : "application/octet-stream";

    const handleFileRemove = () => onChange(field.fieldID, "");

    return (
      <Box sx={wrapperStyle}>
        <Typography variant="body2" sx={{ mb: 1 }}>{label}</Typography>
        <input
          type="file"
          accept="*/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              const reader = new FileReader();
              reader.onload = () => {
                onChange(field.fieldID, reader.result);
              };
              reader.readAsDataURL(file);
            }
          }}
          disabled={isReadOnly}
          aria-label={`Upload file for ${label}`}
        />
        {value && (
          <Box mt={1} display="flex" alignItems="center" gap={2}>
            <Tooltip title="Open in viewer">
              <Button
                size="small"
                variant="text"
                onClick={() => window.open(`/blobViewer`, "_blank", "noopener,noreferrer")}
              >
                View File
              </Button>
            </Tooltip>
            <Typography variant="caption" color="text.secondary">
              {fileMime}
            </Typography>
            <Button size="small" color="error" onClick={handleFileRemove}>
              Remove
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  // === DEFAULT TEXT FIELD ===
  return (
    <Box sx={wrapperStyle}>
      <Box display="flex" alignItems="center" gap={1}>
        {renderStatusIcon()}
        <Box flex={1}>
          {renderFocusedHint()}
          <Tooltip
            title={
              typeof localValue === "string" && localValue.length > 255
                ? localValue.slice(0, 255) + "..."
                : ""
            }
            placement="top-start"
          >
            <TextField
              fullWidth
              label={label}
              value={localValue}
              onChange={handleChange}
              onBlur={handleBlur}
              onFocus={() => setFocused(true)}
              autoFocus={autoFocus}
              required={isRequired}
              error={!!localError}
              margin="dense"
              inputProps={{
                maxLength,
                pattern: inputMask || undefined,
                readOnly: isReadOnly,
                "aria-label": label,
              }}
            />
          </Tooltip>
          {renderHelperBelow()}
        </Box>
      </Box>
    </Box>
  );
};

export default InlineFieldRenderer;
